import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Activity, Download } from "lucide-react";

const Performance = () => {
  // Sample historical data
  const historicalData = [
    {
      time: "08:00",
      responseTime: 235,
      throughput: 1150,
      errorRate: 0.02,
      cpuUsage: 45,
      memoryUsage: 62,
    },
    {
      time: "09:00",
      responseTime: 245,
      throughput: 1200,
      errorRate: 0.015,
      cpuUsage: 48,
      memoryUsage: 65,
    },
    {
      time: "10:00",
      responseTime: 238,
      throughput: 1180,
      errorRate: 0.018,
      cpuUsage: 52,
      memoryUsage: 68,
    },
  ];

  const modules = [
    {
      id: 1,
      name: "SACP Module 1",
      responseTime: 245,
      throughput: 1200,
      errorRate: 0.02,
    },
    {
      id: 2,
      name: "SACP Module 2",
      responseTime: 238,
      throughput: 1180,
      errorRate: 0.015,
    },
    {
      id: 3,
      name: "SACP Module 3",
      responseTime: 242,
      throughput: 1190,
      errorRate: 0.018,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">System Performance</h1>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          <Download size={16} />
          Export Data
        </button>
      </div>

      {/* Key Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600">Avg Response Time</p>
              <p className="text-3xl font-bold text-blue-600">245ms</p>
            </div>
            <Activity className="text-blue-600" size={24} />
          </div>
          <div className="mt-2 text-sm text-gray-500">+2.3% from last hour</div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600">Throughput</p>
              <p className="text-3xl font-bold text-green-600">1,200/s</p>
            </div>
            <Activity className="text-green-600" size={24} />
          </div>
          <div className="mt-2 text-sm text-gray-500">+5% from last hour</div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600">Error Rate</p>
              <p className="text-3xl font-bold text-red-600">0.02%</p>
            </div>
            <Activity className="text-red-600" size={24} />
          </div>
          <div className="mt-2 text-sm text-gray-500">-0.3% from last hour</div>
        </div>
      </div>

      {/* Performance Trends */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Performance Trends</h2>
        <div className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="responseTime"
                stroke="#2563eb"
                name="Response Time (ms)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="throughput"
                stroke="#16a34a"
                name="Throughput (req/s)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="errorRate"
                stroke="#dc2626"
                name="Error Rate (%)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Resource Usage */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Resource Usage</h2>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={historicalData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis domain={[0, 100]} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="cpuUsage"
                stroke="#2563eb"
                name="CPU Usage (%)"
              />
              <Line
                type="monotone"
                dataKey="memoryUsage"
                stroke="#16a34a"
                name="Memory Usage (%)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Module Performance */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Module Performance</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-3 px-4">Module</th>
                <th className="text-right py-3 px-4">Response Time</th>
                <th className="text-right py-3 px-4">Throughput</th>
                <th className="text-right py-3 px-4">Error Rate</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((module) => (
                <tr key={module.id} className="border-b">
                  <td className="py-3 px-4">{module.name}</td>
                  <td className="text-right py-3 px-4">
                    {module.responseTime}ms
                  </td>
                  <td className="text-right py-3 px-4">
                    {module.throughput}/s
                  </td>
                  <td className="text-right py-3 px-4">{module.errorRate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Performance;
